import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Mail, Phone, MapPin, Send, MessageSquare, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBranding } from "../hooks/useBranding";

export default function Contact() {
  const { t } = useTranslation();
  const { branding } = useBranding();

  return (
    <div className="min-h-screen bg-slate-950 text-white font-inter">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute top-0 left-1/4 w-full max-w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter leading-tight font-outfit uppercase">
            {t('contact.title')} <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">{t('contact.title_highlight')}</span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto font-medium leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
            {/* Contact Form */}
            <div className="glass-card p-10 border-white/10 shadow-2xl relative group overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Send size={150} />
                </div>
              <h2 className="text-3xl font-black mb-8 font-outfit uppercase tracking-tight">{t('contact.form_title')}</h2>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('contact.full_name')}</label>
                    <input 
                      type="text" 
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-4 text-white focus:border-indigo-500/50 outline-none transition-all placeholder:text-slate-600 font-medium"
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('contact.email')}</label>
                    <input 
                      type="email" 
                      className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-4 text-white focus:border-indigo-500/50 outline-none transition-all placeholder:text-slate-600 font-medium"
                      placeholder="contact@nova.global"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('contact.subject')}</label>
                  <select className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-4 text-white focus:border-indigo-500/50 outline-none transition-all font-medium appearance-none">
                    <option>{t('contact.subject_tech')}</option>
                    <option>{t('contact.subject_mlm')}</option>
                    <option>{t('contact.subject_invest')}</option>
                    <option>{t('contact.subject_other')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('contact.message')}</label>
                  <textarea 
                    rows="5"
                    className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-4 text-white focus:border-indigo-500/50 outline-none transition-all placeholder:text-slate-600 font-medium"
                    placeholder={t('contact.message_placeholder')}
                  ></textarea>
                </div>
                <button className="btn-premium w-full !py-5 flex items-center justify-center gap-3 shadow-indigo-500/20">
                  <span className="font-black text-sm uppercase tracking-widest">{t('contact.send_request')}</span>
                  <Send size={18} />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col justify-center space-y-12">
                <div className="space-y-8">
                    <h2 className="text-4xl font-black font-outfit uppercase tracking-tight">{t('contact.info_title')} <span className="text-indigo-400 text-sm align-top leading-none tracking-normal font-inter ml-2 lowercase">{t('contact.info_subtitle')}</span></h2>
                    <p className="text-slate-400 font-medium leading-relaxed text-lg whitespace-pre-line">
                        {branding.page_contact_content || t('contact.info_desc')}
                    </p>
                </div>

              <div className="space-y-6">
                {[
                  { icon: <Mail />, title: t('contact.support_general'), detail: branding.support_email, color: "text-indigo-400" },
                  { icon: <Phone />, title: t('contact.direct_line'), detail: "+971 4 123 4567", color: "text-cyan-400" },
                  { icon: <MapPin />, title: t('contact.headquarters'), detail: branding.office_address, color: "text-emerald-400" },
                  { icon: <MessageSquare />, title: t('contact.live_chat'), detail: t('contact.live_chat_desc'), color: "text-amber-400" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-6 group">
                    <div className={`mt-1 p-3 bg-white/5 rounded-xl border border-white/5 ${item.color} group-hover:scale-110 transition-transform`}>
                      {React.cloneElement(item.icon, { size: 24 })}
                    </div>
                    <div>
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">{item.title}</p>
                      <p className="text-lg font-bold text-white tracking-tight">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-8 glass-card border-indigo-500/10 flex items-center gap-6">
                  <div className="p-3 bg-indigo-500/10 rounded-full text-indigo-400">
                    <ShieldCheck size={32} />
                  </div>
                  <p className="text-sm font-semibold text-slate-400 leading-relaxed">
                    {t('contact.security_notice')}
                  </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
